import { useState } from "react";
import { Loader2 } from "lucide-react";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useAssignRole, useRevokeRole } from "@/hooks/use-users";
import { useAuth } from "@/auth/useAuth";
import type { OrgRole } from "@/lib/permissions";

interface OrgUser {
  uid: string;
  email: string;
  displayName: string;
  role: string;
}

const ROLE_OPTIONS: { value: OrgRole; label: string }[] = [
  { value: "org_admin", label: "Org Admin" },
  { value: "data_manager", label: "Data Manager" },
  { value: "cra", label: "CRA" },
  { value: "pi", label: "Principal Investigator" },
  { value: "site_coordinator", label: "Site Coordinator" },
  { value: "viewer", label: "Viewer" },
];

export function UserInspector({ data }: { data: unknown }) {
  const user = data as OrgUser;
  const { can } = useAuth();
  const assignRole = useAssignRole();
  const revoke = useRevokeRole();
  const [selectedRole, setSelectedRole] = useState(user.role);
  const isAdmin = can("connections:manage");

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          {user.displayName || "Unnamed User"}
        </h3>
        <p className="mt-0.5 text-sm text-muted-foreground">{user.email}</p>
        <div className="mt-2">
          <StatusBadge status={user.role} />
        </div>
      </div>

      <div className="space-y-2">
        <Detail label="UID" value={user.uid} />
        <Detail label="Email" value={user.email} />
        <Detail label="Role" value={ROLE_OPTIONS.find((r) => r.value === user.role)?.label ?? user.role} />
      </div>

      {isAdmin && (
        <div className="space-y-4 border-t border-border pt-4">
          <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Manage Role
          </h4>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Assign Role
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() =>
                assignRole.mutate({ userId: user.uid, role: selectedRole })
              }
              disabled={assignRole.isPending || selectedRole === user.role}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {assignRole.isPending && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              Update Role
            </button>
            <button
              onClick={() => {
                if (confirm("Revoke access for this user?")) {
                  revoke.mutate({ userId: user.uid });
                }
              }}
              disabled={revoke.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {revoke.isPending && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              Revoke Access
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground break-all">{value}</p>
    </div>
  );
}
